/**
 * useShellActionDrum - the action drum (v8.218.0; decisive-only since v8.236.0).
 *
 * Owns the drum's open state and its CHAMBER - the decisive action the big
 * trigger fires instead of the plain attack (null = ATTACK, or the console's
 * called shot; engine/actions/chamber ranks the claims) - and answers:
 *
 * - `drumDisabledReasons`: why each plate is dimmed. Eligibility is pure and
 *   lives in engine/actions/eligibility (v8.300.0).
 * - `attackTriggerLabel` (v8.232.0): a drum-chambered action outranks the
 *   console's called shot outranks ATTACK - one source, never a lie.
 * - `actionDrumProps`: what the drum component renders and presses.
 * - `fireRef`: the caller points it at the plain attack; `engageAndFire`
 *   plates pull it once their stance or mode has LANDED (v8.301.0), since
 *   setState is not synchronous and the roll must read the new record.
 *
 * An invalid chamber quietly reverts to ATTACK - the trigger must never lie
 * about what firing it does. Extracted from DeathWatchSimulator.
 */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { DRUM_CATALOGS, actionById, rowsForBody } from '../engine/actions/catalog';
import { STANCE_PLATE_PREFIX, withStancePlates } from '../engine/actions/stancePlates';
import { upNow } from '../engine/character/soloModes';
import { triggerLabel } from '../engine/actions/chamber';
import { chamberFor } from '../engine/actions/types';
import { COST_POINTS, chargeForPress } from '../engine/actions/economy';
import { applyStanceToggle, setStanceToggle } from '../engine/actions/toggleEntries';
import { drumDisabledReasons as computeDrumDisabledReasons } from '../engine/actions/eligibility';
import { getBodyConfig } from '../engine/bodyConfig';
import { NOTCH_AT_REST, turnNotch } from '../engine/actions/difficultyNotch';
import { hasCondition } from './shellCombatant';
import useActorSwitchReset from './useActorSwitchReset';
import useStableCallback from './useStableCallback';
import useStableValue from './useStableValue';

/** The plates this drum is loaded with: the phase's (`PHASE_LAYOUT.drum`), Combat's when unsaid. */
const defsFor = (drum) => DRUM_CATALOGS[drum] ?? DRUM_CATALOGS.combat;

/**
 * The phase's plates (a vehicle's own only on a vehicle: `rowsForBody`), then
 * the acting character's own stances (`engine/actions/stancePlates`). The
 * catalog array itself for a character with none, and one array per (drum,
 * body, sheet's stances) otherwise.
 */
function useDrumDefs(drum, actor, isVehicle, squad) {
  const stored = actor?.sheet?.soloModes;
  return useMemo(() => {
    const own = withStancePlates(rowsForBody(defsFor(drum), !!isVehicle), stored);
    // A brother's Squad Mode run, after his stances: appended, so nothing before it moves (killTeamPlates).
    return squad?.defs.length ? [...own, ...squad.defs] : own;
  }, [drum, isVehicle, stored, squad]);
}

/** A target's body type: its own, else a horde's, else humanoid. */
export function bodyTypeOf(target) {
  return target?.bodyType || (target?.horde?.isHorde ? 'horde' : 'humanoid');
}

/** The trigger's face for this chamber, called shot and target. */
export function triggerFor(chamberedActionId, calledShot, target, targetLocation) {
  const locationName = getBodyConfig(bodyTypeOf(target)).locations.find((l) => l.id === targetLocation)?.name ?? null;
  return triggerLabel({
    chamberedName: chamberedActionId ? (actionById(chamberedActionId)?.name ?? null) : null,
    calledShot: !!calledShot,
    locationName,
  });
}

/** Is the state this plate engages already held? (What a press is charged against.) */
function engagedBefore(def, attackToggles, defensiveStanceActive) {
  return def.engages?.toggleKey
    ? !!attackToggles[def.engages.toggleKey]
    : def.engages?.conditionCode === 'Ds' && defensiveStanceActive;
}

/** `engage`: write the state and stop. Pressing an engaged one again drops it. */
function engage(def, ops) {
  const key = def.engages?.toggleKey;
  if (key) ops.setAttackToggles((t) => applyStanceToggle(t, key));
  if (def.engages?.conditionCode === 'Ds') ops.handleToggleDefensiveStance();
}

/** `engageAndFire`: engage (never flip - "all-out" must mean all-out) and swing once it lands. */
function engageAndFire(def, ops) {
  const key = def.engages?.toggleKey;
  if (key) {
    ops.setAttackToggles((t) => setStanceToggle(t, key, true));
    ops.setStanceFireKey(key);
  }
  if (def.engages?.attackMode) {
    ops.setAttackMode(def.engages.attackMode);
    ops.setModeFirePending(true);
  }
}

/**
 * Pressing a plate does exactly what its `execution` says - exhaustive over
 * that vocabulary rather than over ids. `chamber` loads the trigger
 * (Standard Attack chambers the default, i.e. clears it; cost is logged on
 * the FIRE, since chambering is intent); `instant` resolves on the spot.
 */
const EXECUTIONS = {
  chamber: (def, ops) => ops.setChamberedActionId(chamberFor(def)),
  engage,
  engageAndFire,
  instant: (def, ops) => ops.instantActions[def.id]?.(),
  ownStance: (def, ops) => ops.toggleStance?.(def.stanceId),
  // The kill-team's own press (useKillTeamDrum): the verb, and its action charged once it has taken.
  killTeam: (def, ops) => ops.pressSquad?.(def.id),
};

/**
 * One press of a plate. ADVISORY COST (v8.309.0, corrected in v8.309.2): the
 * charge follows the STATE CHANGE, not the press - putting a stance back
 * down refunds the press that took it up (engine/actions/economy).
 */
export function pressPlate(id, ops) {
  // The drum's own plates first: a character's stance plates are in no catalog.
  const def = ops.defs?.find((d) => d.id === id) ?? actionById(id);
  if (!def) return;
  if (def.execution === 'instant') pressInstant(def, ops);
  else {
    if (ops.actor && def.execution !== 'killTeam') {
      const held = engagedBefore(def, ops.attackToggles, ops.defensiveStanceActive);
      ops.spendTurnRef.current?.(ops.actor.id, chargeForPress(def, held));
    }
    EXECUTIONS[def.execution]?.(def, ops);
  }
  ops.setActionDrumOpen(false);
}

/**
 * An instant plate is charged AFTER it resolves, at the cost it answers with when it knows better than its row
 * (2026-10-26): Stand is a Half, or nothing with Leap Up - the pips used to take the row's Half either way.
 */
function pressInstant(def, ops) {
  const out = EXECUTIONS.instant(def, ops);
  if (!ops.actor) return;
  const points = out && typeof out === 'object' && out.cost ? COST_POINTS[out.cost] : chargeForPress(def, false);
  ops.spendTurnRef.current?.(ops.actor.id, points);
}

/** The waits: the swing, once the stance is on the record; the same for a MODE's -20. */
function useDeferredFire(attackToggles, attackMode) {
  const [stanceFireKey, setStanceFireKey] = useState(null);
  const [modeFirePending, setModeFirePending] = useState(false);
  const fireRef = useRef(null);
  useEffect(() => {
    if (!stanceFireKey || !attackToggles[stanceFireKey]) return;
    setStanceFireKey(null);
    fireRef.current?.();
  }, [stanceFireKey, attackToggles]);
  useEffect(() => {
    if (!modeFirePending || !attackMode) return;
    setModeFirePending(false);
    fireRef.current?.();
  }, [modeFirePending, attackMode]);
  return { fireRef, setStanceFireKey, setModeFirePending };
}

/** Why each plate is dimmed, and the chamber's revalidation against it. */
function useDisabledReasons(args, chambered, drumDefs) {
  const { actor, actorConds, currentTab, isLocked, isMelee, fireMode, targetUnaware } = args;
  const { outOfReach, notInContact, offBow, ladderKnows, chargeRefusal, notEngaged } = args;
  const targetIsDead = hasCondition(currentTab?.target?.conditions, 'K');
  const actorIsProne = !!actor && hasCondition(actorConds, 'P');
  const actorIsPinned = !!actor && hasCondition(actorConds, 'Pn');
  const drumDisabledReasons = useMemo(
    () =>
      computeDrumDisabledReasons(drumDefs, {
        locked: isLocked('attack'),
        isMelee,
        hasActor: !!actor,
        hasTarget: !!currentTab,
        targetIsDead,
        actorIsProne,
        isFullAuto: fireMode === 'full',
        hasBearing: !!currentTab,
        targetUnaware: !!targetUnaware,
        ladderKnows: !!ladderKnows,
        outOfReach: !!outOfReach,
        notInContact: !!notInContact,
        offBow: !!offBow,
        chargeRefusal: chargeRefusal ?? null,
        actorIsPinned,
        notEngaged: !!notEngaged,
      }),
    // prettier-ignore
    [drumDefs, isMelee, actor, isLocked, currentTab, targetIsDead, actorIsProne, actorIsPinned, fireMode, targetUnaware, ladderKnows, outOfReach, notInContact, offBow, chargeRefusal, notEngaged],
  );
  // The Squad Mode run says its own reasons (engine/actions/killTeamPlates); the catalog's rules never dim it.
  const squadReasons = args.squad?.reasons;
  const withSquad = useMemo(
    () => (squadReasons ? { ...drumDisabledReasons, ...squadReasons } : drumDisabledReasons),
    [drumDisabledReasons, squadReasons],
  );
  useChamberRevert(chambered, drumDefs, withSquad);
  return withSquad;
}

/**
 * A chamber that may no longer fire reverts to ATTACK: its plate went dim, or the phase changed and this drum no
 * longer holds it.
 */
function useChamberRevert([chamberedActionId, setChamberedActionId], drumDefs, drumDisabledReasons) {
  useEffect(() => {
    if (!chamberedActionId) return;
    const stillHere = drumDefs.some((d) => d.id === chamberedActionId);
    if (!stillHere || drumDisabledReasons[chamberedActionId]) setChamberedActionId(null);
  }, [chamberedActionId, drumDefs, drumDisabledReasons, setChamberedActionId]);
}

/** The press callback, bound to this render's toggles and stance; `ops` are the drum's own (stable) setters. */
function useDrumExecute(args, ops, defs) {
  const { actor, attackToggles, defensiveStanceActive, setAttackToggles, handleToggleDefensiveStance } = args;
  const { instantActions, setAttackMode, spendTurnRef, toggleStance, pressSquad } = args;
  return useCallback(
    (id) =>
      pressPlate(id, {
        ...ops,
        defs,
        toggleStance,
        pressSquad,
        actor,
        attackToggles,
        defensiveStanceActive,
        spendTurnRef,
        setAttackToggles,
        handleToggleDefensiveStance,
        instantActions,
        setAttackMode,
      }),
    [
      ops,
      defs,
      toggleStance,
      pressSquad,
      setAttackToggles,
      handleToggleDefensiveStance,
      instantActions,
      actor,
      attackToggles,
      defensiveStanceActive,
      spendTurnRef,
      setAttackMode,
    ],
  );
}

/** The ids of the acting character's Solo-mode plates that are up (none in Squad Mode). */
function standingPlates(actor) {
  if (!actor?.sheet?.soloModes) return [];
  return upNow(actor.sheet, actor.state).map((u) => `${STANCE_PLATE_PREFIX}${u.id}`);
}

/** Which plates show ENGAGED: a held toggle, the Ds condition, or one of the character's own stances that is up. */
function engagedIds(drumDefs, attackToggles, defensiveStanceActive, actor, squad) {
  const ids = drumDefs.filter((d) => d.engages?.toggleKey && attackToggles[d.engages.toggleKey]).map((d) => d.id);
  if (defensiveStanceActive) ids.push('defensiveStance');
  // Only plates this drum holds: a triggered stance is up with no plate to light.
  const held = new Set(drumDefs.map((d) => d.id));
  return [...ids, ...standingPlates(actor).filter((id) => held.has(id)), ...(squad?.engaged ?? [])];
}

/** The notch as the drum draws it: the value, who may turn the other side, and the turn itself. */
function useNotchProps({ notch, setNotch, canSetTheirs }) {
  const onTurn = useCallback(
    (side, mod) => setNotch?.((was) => turnNotch(was, side, mod, !!canSetTheirs)),
    [setNotch, canSetTheirs],
  );
  return useMemo(
    () => ({ value: notch ?? NOTCH_AT_REST, canSetTheirs: !!canSetTheirs, onTurn }),
    [notch, canSetTheirs, onTurn],
  );
}

/** What the drum component renders and presses. */
function useDrumProps(args, drumDefs, chamberedActionId, drumDisabledReasons, onExecute) {
  const { attackToggles, defensiveStanceActive, actor, squad } = args;
  const notch = useNotchProps(args);
  const drumEngagedIds = useStableValue(
    useMemo(
      () => engagedIds(drumDefs, attackToggles, defensiveStanceActive, actor, squad),
      [drumDefs, attackToggles, defensiveStanceActive, actor, squad],
    ),
  );
  return useMemo(
    () => ({
      defs: drumDefs,
      chamberedId: chamberedActionId,
      disabledReasons: drumDisabledReasons,
      engagedIds: drumEngagedIds,
      onExecute,
      notch,
    }),
    [drumDefs, chamberedActionId, drumDisabledReasons, drumEngagedIds, onExecute, notch],
  );
}

/**
 * @param {object} args - actor, actorConds, currentTab, isLocked, isMelee,
 *   fireMode, targetLocation, attackToggles, setAttackToggles, attackMode,
 *   setAttackMode, defensiveStanceActive, handleToggleDefensiveStance,
 *   instantActions (by id), spendTurnRef (the advisory action-economy spender),
 *   notch, setNotch, canSetTheirs (the difficulty notch - engine/actions/difficultyNotch),
 *   drum (which plates: 'combat' | 'stealth', from the phase layout), targetUnaware, ladderKnows (why nobody is),
 *   toggleStance (switches one of the acting character's Solo modes; their plates are appended),
 *   squad and pressSquad (a brother's Squad Mode run and its press - hooks/useKillTeamDrum; appended after).
 * @returns the drum's state and answers - see the module header.
 */
export default function useShellActionDrum(args) {
  const { currentTab, targetLocation, attackToggles, attackMode } = args;
  const [actionDrumOpen, setActionDrumOpen] = useState(false);
  const chambered = useState(null);
  const [chamberedActionId, setChamberedActionId] = chambered;
  // A switch of actor un-chambers: a loaded action is the actor's, not the screen's (2026-10-22).
  useActorSwitchReset(args.actor?.id ?? null, () => setChamberedActionId(null));
  // The plates and why each is off, the same while they read the same (plan shell-contexts, A3): both are rebuilt
  // with every write to the actor, and the attacker context holding the drum's props changed with each one.
  const drumDefs = useStableValue(useDrumDefs(args.drum, args.actor, args.actorIsVehicle, args.squad));
  const drumDisabledReasons = useStableValue(useDisabledReasons(args, chambered, drumDefs));
  const attackTriggerLabel = useMemo(
    () => triggerFor(chamberedActionId, attackToggles.calledShot, currentTab?.target, targetLocation),
    [currentTab, targetLocation, attackToggles.calledShot, chamberedActionId],
  );
  const { fireRef, setStanceFireKey, setModeFirePending } = useDeferredFire(attackToggles, attackMode);
  const ops = useMemo(
    () => ({ setChamberedActionId, setActionDrumOpen, setStanceFireKey, setModeFirePending }),
    [setChamberedActionId, setStanceFireKey, setModeFirePending],
  );
  // A press, never read while drawing: stable, calling the latest.
  const onExecute = useStableCallback(useDrumExecute(args, ops, drumDefs));
  const actionDrumProps = useDrumProps(args, drumDefs, chamberedActionId, drumDisabledReasons, onExecute);
  const toggleActionDrum = useCallback(() => setActionDrumOpen((o) => !o), []);
  const drum = { actionDrumOpen, setActionDrumOpen, toggleActionDrum, chamberedActionId, setChamberedActionId };
  return { ...drum, drumDisabledReasons, attackTriggerLabel, actionDrumProps, fireRef };
}
